export interface PostFeedCacheEntry {
  feed: any[]
  updatedAt: number
  expiresAt: number
  nextPageParam?: any | null
  hasNextPage?: boolean
}

export const updatePostInFeedCache = <
  T extends Record<string, PostFeedCacheEntry>
>(
  feedCache: T,
  updatedPost: any
): T => {
  if (!updatedPost?.id) {
    return feedCache
  }

  let changed = false

  const nextFeedCache = Object.fromEntries(
    Object.entries(feedCache).map(([cacheKey, entry]) => {
      if (!Array.isArray(entry?.feed)) {
        return [cacheKey, entry]
      }

      const postIndex = entry.feed.findIndex(
        post =>
          String(post?.id) ===
          String(updatedPost.id)
      )

      if (postIndex === -1) {
        return [cacheKey, entry]
      }

      changed = true

      const nextFeed = [...entry.feed]

      nextFeed[postIndex] = updatedPost

      return [
        cacheKey,
        {
          ...entry,
          feed: nextFeed,
          updatedAt: Date.now()
        }
      ]
    })
  ) as T

  return changed
    ? nextFeedCache
    : feedCache
}

export const updatePostInQueryData = (
  value: any,
  updatedPost: any
): any => {
  if (
    value == null ||
    !updatedPost?.id
  ) {
    return value
  }

  if (Array.isArray(value)) {
    let changed = false

    const next = value.map(item => {
      const updated =
        updatePostInQueryData(
          item,
          updatedPost
        )

      if (updated !== item) {
        changed = true
      }

      return updated
    })

    return changed
      ? next
      : value
  }

  if (typeof value !== 'object') {
    return value
  }

  const isPost =
    String(value?.id) ===
      String(updatedPost.id) &&
    (
      Object.prototype.hasOwnProperty.call(
        value,
        'body'
      ) ||
      Object.prototype.hasOwnProperty.call(
        value,
        'photos'
      ) ||
      Object.prototype.hasOwnProperty.call(
        value,
        'videos'
      ) ||
      Object.prototype.hasOwnProperty.call(
        value,
        'sounds'
      )
    )

  if (isPost) {
    return updatedPost
  }

  let changed = false
  const next: Record<string, any> = {}

  for (
    const [key, child]
    of Object.entries(value)
  ) {
    const updated =
      updatePostInQueryData(
        child,
        updatedPost
      )

    next[key] = updated

    if (updated !== child) {
      changed = true
    }
  }

  return changed
    ? next
    : value
}

export const isPostQueryKey = (
  queryKey: unknown
): boolean => {
  if (!Array.isArray(queryKey)) {
    return false
  }

  const postQueryKeys = new Set([
    'getFeed',
    'getSinglePost',
    'getMediaData',
    'profileFeed',
    'getProfileFeed'
  ])

  return queryKey.some(part =>
    postQueryKeys.has(
      String(part)
    )
  )
}

export const addPostToFeedCache = <
  T extends Record<string, PostFeedCacheEntry>
>(
  feedCache: T,
  newPost: any,
  userId?: string
): T => {
  if (!newPost?.id) return feedCache

  let changed = false

  const nextFeedCache = Object.fromEntries(
    Object.entries(feedCache).map(([cacheKey, entry]) => {
      if (!Array.isArray(entry?.feed)) {
        return [cacheKey, entry]
      }

      if (
        userId &&
        String(newPost?.user_id ?? newPost?.user?.id) !== String(userId)
      ) {
        return [cacheKey, entry]
      }

      const alreadyExists = entry.feed.some(
        post => String(post?.id) === String(newPost.id)
      )

      if (alreadyExists) {
        return [cacheKey, entry]
      }

      changed = true

      return [
        cacheKey,
        {
          ...entry,
          feed: [newPost, ...entry.feed],
          updatedAt: Date.now()
        }
      ]
    })
  ) as T

  return changed ? nextFeedCache : feedCache
}